import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { Session } from '../../api/session';
import { getPass, type Banner, type ObjectInfo } from '../../api/tenant';
import { passPageLink, passReadyMessage } from '../../domain/invite';
import { moscowDayLabel, moscowTime } from '../../domain/moscowTime';
import type { TelegramApi } from '../../telegram/webapp';
import { BannerSlot } from '../../ui/BannerSlot';
import { Plate } from '../../ui/Plate';
import type { HomeOrderDone } from './useHomeOrder';
import s from './homeB.module.css';

interface Props {
  done: HomeOrderDone;
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
  banners: readonly Banner[];
  onClose: () => void;
}

/** Receipt after an order from the home screen: what exactly was ordered, so nobody calls the УК to check. */
export function OrderDone({ done, session, telegram, object, banners, onClose }: Props) {
  const navigate = useNavigate();
  // The created pass carries no sharedToken in every response, so the guest page link is read from the pass itself.
  const share = useMutation({
    mutationFn: (passId: number) => getPass(session, passId),
    onSuccess: (pass) => {
      const link = passPageLink(pass.sharedToken);
      if (link) telegram.share(link, passReadyMessage(object.name));
      else void navigate(`/passes/${pass.id}`);
    },
    onError: (_e, passId) => void navigate(`/passes/${passId}`),
  });
  const until = done.expiresAt ? `${moscowDayLabel(done.expiresAt, new Date())}, до ${moscowTime(done.expiresAt)}` : 'бессрочно';

  return (
    <main className={s.page}>
      <div className={s.doneHead}>
        <span className={done.request ? `${s.check} ${s.wait}` : s.check} aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            {done.request ? <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></> : <path d="M5 12.5l4.5 4.5L19 7.5" />}
          </svg>
        </span>
        <h1>{done.request ? 'Заявка отправлена' : 'Пропуск оформлен'}</h1>
      </div>
      <p className={s.doneText}>
        {done.request
          ? 'УК рассмотрит заявку, статус — в разделе «Обращения».'
          : done.guest.kind === 'vehicle'
            ? 'Охрана уже видит пропуск. Гость проедет по номеру — ничего отправлять не нужно.'
            : 'Охрана уже видит пропуск и пропустит гостя по имени.'}
      </p>
      <div className={s.receipt}>
        <div className={s.row}>
          <span>Гость</span>
          <span>{done.guest.kind === 'vehicle' ? <Plate value={done.guest.plate} /> : done.guest.name}</span>
        </div>
        <div className={s.row}>
          <span>Когда</span>
          <span>{until}</span>
        </div>
        <div className={s.row}>
          <span>Адрес</span>
          <span>{done.addressName}</span>
        </div>
      </div>
      <button type="button" className={s.order} onClick={onClose}>
        Готово
      </button>
      {done.passId !== null && (
        <button type="button" className={s.second} disabled={share.isPending} onClick={() => share.mutate(done.passId!)}>
          {share.isPending ? 'Готовим ссылку…' : 'Отправить гостю'}
        </button>
      )}
      <BannerSlot banners={banners} telegram={telegram} placement="success" />
    </main>
  );
}
